import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadPathApi } from '../../api/uploadPath';
import { plottrackPath } from '../../utils/plottrackPath';
import OutsideChatPromptChooser from './OutsideChatPromptChooser';
import {
  ADD_SCENES_TO_PLOT_TRACK,
  IMPORT_SCENES_NEED_BOOK,
  OPEN_PLOT_TRACK,
  PICK_A_FRAMEWORK,
  SCENE_LIST_KEEP_ONE,
  SCENES_ON_PLOT_TRACK,
  UPLOAD_PATH_A_NEXT,
  UPLOAD_PATH_B_NEXT,
  UPLOAD_PATH_LEGACY,
  UPLOAD_PATH_NO_SCENES,
  WORK_SCENES_IN_PLOT_FOCUS,
} from '../../utils/writerCopy';
import './UploadPathOffers.css';

/**
 * Checklist door beside Story-order Offer 1 (#1266).
 * A: digest approve already put the list on Plot Grid. This door opens
 * that track, or places the list if approve could not.
 * B: pick a Framework, then work scenes in Plot Focus.
 * Legacy v1: regenerate with A or B. It is not treated as a scene list.
 */
export default function UploadPathOffers({ projectId, storyId, batchIds }) {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [edits, setEdits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [alreadyLanded, setAlreadyLanded] = useState(false);
  const batchKey = (batchIds || []).join(',');

  useEffect(() => {
    if (!projectId || !batchKey) {
      setPreview(null);
      setEdits([]);
      return undefined;
    }
    let cancelled = false;
    setError(null);
    Promise.resolve(uploadPathApi.preview(projectId, { batchIds: batchKey.split(',') }))
      .then((data) => {
        if (cancelled) return;
        const next = data || null;
        setPreview(next);
        setEdits((next?.scenes || []).map((scene) => ({
          source_name: scene.source_name || scene.name,
          name: scene.name || '',
          purpose: scene.purpose || '',
        })));
      })
      .catch(() => {
        if (!cancelled) {
          setPreview(null);
          setEdits([]);
        }
      });
    return () => { cancelled = true; };
  }, [projectId, batchKey]);

  useEffect(() => {
    if (!storyId) {
      setAlreadyLanded(false);
      return undefined;
    }
    let cancelled = false;
    Promise.resolve(uploadPathApi.view(storyId))
      .then((data) => {
        if (cancelled) return;
        const onTrack = Boolean(data?.has_import)
          && data?.gaps?.path_kind === 'A'
          && (data?.scenes || []).length > 0;
        setAlreadyLanded(onTrack);
      })
      .catch(() => {
        if (!cancelled) setAlreadyLanded(false);
      });
    return () => { cancelled = true; };
  }, [storyId]);

  const move = (index, delta) => {
    setEdits((rows) => {
      const target = index + delta;
      if (target < 0 || target >= rows.length) return rows;
      const next = rows.slice();
      const [row] = next.splice(index, 1);
      next.splice(target, 0, row);
      return next;
    });
  };

  const rename = (index, name) => {
    setEdits((rows) => rows.map((row, i) => (i === index ? { ...row, name } : row)));
  };

  const drop = (index) => {
    setEdits((rows) => rows.filter((_, i) => i !== index));
  };

  const commit = async () => {
    if (!storyId || !projectId || busy || edits.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await uploadPathApi.importScenes(storyId, {
        projectId,
        batchIds: batchKey.split(','),
        scenes: edits,
      });
      navigate(plottrackPath(storyId));
    } catch (err) {
      setError(err?.message || 'Could not update the scene list.');
    } finally {
      setBusy(false);
    }
  };

  const pickFramework = () => {
    navigate('/frameworks', { state: { uploadPath: 'B' } });
  };

  if (!batchKey || !preview) return null;

  if (preview.legacy_prompt) {
    return (
      <section className="upload-path so-section" aria-label="Older prompt">
        <p className="so-lede">{UPLOAD_PATH_LEGACY}</p>
        <OutsideChatPromptChooser />
      </section>
    );
  }

  if (preview.offer_framework) {
    return (
      <section className="upload-path so-section" aria-label="Story notes">
        <p className="so-lede">{UPLOAD_PATH_B_NEXT}</p>
        <div className="upload-path__actions">
          <button type="button" className="bs-btn bs-btn--primary" onClick={pickFramework}>
            {PICK_A_FRAMEWORK}
          </button>
          <button
            type="button"
            className="bs-btn bs-btn--secondary"
            onClick={() => navigate('/dossiers')}
          >
            {WORK_SCENES_IN_PLOT_FOCUS}
          </button>
        </div>
      </section>
    );
  }

  if (preview.prompt_path_kind === 'A' && !preview.offer_import) {
    return (
      <section className="upload-path so-section" aria-label="Scene list">
        <p className="so-lede">{UPLOAD_PATH_NO_SCENES}</p>
      </section>
    );
  }

  if (!preview.offer_import) return null;

  if (alreadyLanded && storyId) {
    return (
      <section className="upload-path so-section" aria-label="Scene list">
        <p className="so-lede">{SCENES_ON_PLOT_TRACK}</p>
        <div className="upload-path__actions">
          <button
            type="button"
            className="bs-btn bs-btn--primary"
            onClick={() => navigate(plottrackPath(storyId))}
          >
            {OPEN_PLOT_TRACK}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="upload-path so-section" aria-label="Scene list">
      <p className="so-lede">{UPLOAD_PATH_A_NEXT}</p>
      {!storyId && <p className="so-lede">{IMPORT_SCENES_NEED_BOOK}</p>}
      {edits.length === 0 && <p className="so-lede">{SCENE_LIST_KEEP_ONE}</p>}
      <ol className="upload-path__scenes">
        {edits.map((scene, index) => (
          <li key={`${scene.source_name}-${index}`} className="upload-path__scene">
            <label className="upload-path__name">
              <span>Scene name</span>
              <input
                value={scene.name}
                onChange={(event) => rename(index, event.target.value)}
              />
            </label>
            {scene.purpose ? <p className="upload-path__purpose">{scene.purpose}</p> : null}
            <div className="upload-path__row-actions">
              <button type="button" onClick={() => move(index, -1)} disabled={index === 0}>
                Move up
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === edits.length - 1}
              >
                Move down
              </button>
              <button type="button" onClick={() => drop(index)}>
                Remove
              </button>
            </div>
          </li>
        ))}
      </ol>
      {error && <p className="so-lede" role="alert">{error}</p>}
      <div className="upload-path__actions">
        <button
          type="button"
          className="bs-btn bs-btn--primary"
          onClick={commit}
          disabled={!storyId || busy || edits.length === 0}
        >
          {busy ? 'Saving…' : ADD_SCENES_TO_PLOT_TRACK}
        </button>
      </div>
    </section>
  );
}
